import React, { useState } from 'react';
import { Avatar } from '../components/Avatar';
import { Sidebar } from '../components/navigation/Sidebar';
import WelcomeBanner from '../components/WelcomeBanner';
import { AppChromeContext } from './appChromeContext';

const SIDEBAR_COLLAPSED_KEY = 'admin_sidebarCollapsed';

interface LayoutProps {
    children: React.ReactNode;
    /** Drops the welcome banner row for pages that carry their own header. */
    hideHeader?: boolean;
    /**
     * Puts the avatar back as a top-right overlay on a `hideHeader` page, the
     * way the dashboard renders it. Opt-in, because the other headerless pages
     * put their own action buttons in that corner. The page is responsible for
     * keeping the corner clear (resources pages use `reserveTopRight`).
     */
    overlayAvatar?: boolean;
}

export const SidebarLayout: React.FC<LayoutProps> = ({
    children,
    hideHeader = false,
    overlayAvatar = false
}) => {
    const [collapsed, setCollapsed] = useState(() => {
        return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    });

    const [chromeHidden, setChromeHidden] = useState(false);

    const handleToggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
    };

    return (
        <div className="flex w-full h-screen bg-gray-100">
            <Sidebar collapsed={collapsed} onToggle={handleToggle} hidden={chromeHidden} />
            <main className="flex-1 overflow-auto scrollbar-hide">
                <div className="flex flex-col h-full w-full">
                    {!hideHeader && !chromeHidden && (
                        <div className="flex items-center justify-between px-8 pt-4 pb-2 shrink-0">
                            <WelcomeBanner />
                            <div className="w-10 h-10">
                                <Avatar />
                            </div>
                        </div>
                    )}
                    <div className="relative flex-1 min-h-0 flex flex-col">
                        {hideHeader && overlayAvatar && !chromeHidden && (
                            <div className="absolute top-4 right-8 z-10 w-10 h-10">
                                <Avatar />
                            </div>
                        )}
                        <AppChromeContext.Provider value={setChromeHidden}>
                            {children}
                        </AppChromeContext.Provider>
                    </div>
                </div>
            </main>
        </div>
    );
};
